import React from 'react';
import { graphql, useFragment } from 'react-relay';
import { MENU_ID_APPS } from '@jira/navigation-apps__sidebar-nav4-context/src/common/constants.tsx';
import { useSidebarNav4 } from '@jira/navigation-apps__sidebar-nav4-context/src/controllers/sidebar-context/index.tsx';
import { useIsGeneralConnectRoute } from '@jira/navigation-apps__sidebar-nav4-sidebars-common-core/src/common/utils/use-is-apps-route/index.tsx';
import type { Nav4DragAndDropMenuItemProps } from '@jira/navigation-apps__sidebar-nav4-sidebars-common-core/src/common/types.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { SectionActions } from '../../common/ui/section-actions/SectionActions.tsx';
import { useIsGlobalConnectAppRoute } from '../../controllers/use-is-connect-route/index.tsx';
import type { Nav4Apps$key } from './__generated__/Nav4Apps.graphql.ts';
import type { Nav4Apps_GlobalAppsFragment$key } from './__generated__/Nav4Apps_GlobalAppsFragment.graphql.ts';
import { Nav4AppsDefault } from './default/Nav4AppsDefault.tsx';
import { Nav4AppsForLanding } from './for-landing/Nav4AppsForLanding.tsx';
import { Nav4AppsSpotlightWrapper } from './Nav4AppsSpotlightWrapper.tsx';

export type Nav4AppsProps = Nav4DragAndDropMenuItemProps & {
	isExpanded: boolean;
	queryRef: Nav4Apps$key;
};

export function Nav4Apps(props: Nav4AppsProps): React.JSX.Element {
	return (
		<Nav4AppsSpotlightWrapper>
			<Nav4AppsInner {...props} />
		</Nav4AppsSpotlightWrapper>
	);
}

function Nav4AppsInner(props: Nav4AppsProps) {
	const { isExpanded, queryRef, ...dndProps } = props;
	const { isInitialSelectedPath } = useSidebarNav4();

	// eslint-disable-next-line @atlassian/relay/use-fragment-naming, @atlassian/relay/use-fragment-naming
	const data = useFragment<Nav4Apps$key>(
		graphql`
			fragment Nav4Apps on Query
			@argumentDefinitions(skipOrderQuery: { type: "Boolean!", defaultValue: false }) {
				jira @required(action: THROW) {
					...Nav4AppsForLanding @dangerously_unaliased_fixme @include(if: $isAppsExpanded)
					...Nav4Apps_GlobalAppsFragment
				}
				appsOrder: settings_navigationCustomisation(entityAri: $entityAriApps, ownerAri: $ownerAri)
					@skip(if: $skipOrderQuery)
					@include(if: $isAppsExpanded) {
					# This fragment is used by Nav4AppsForLanding which receives this data as a prop
					# eslint-disable-next-line @atlassian/relay/must-colocate-fragment-spreads
					...Nav4AppsContent_navigationCustomisation
				}
			}
		`,
		// eslint-disable-next-line @atlassian/relay/use-fragment-naming
		queryRef,
	);

	/* eslint-disable @atlassian/relay/unused-fields */
	const globalAppNavigationItems = useFragment<Nav4Apps_GlobalAppsFragment$key>(
		graphql`
			fragment Nav4Apps_GlobalAppsFragment on JiraQuery {
				globalAppNavigationItems(cloudId: $cloudId) @include(if: $isAppsExpanded) {
					edges {
						node {
							... on JiraAppNavigationItem {
								appType
								url
								sections {
									links {
										url
									}
								}
							}
						}
					}
				}
			}
		`,
		data.jira,
	);

	// $isAppsExpanded (in the query) is based on the same logic as in isInitialSelectedPath(MENU_ID_APPS).
	// However, below we filter out false positives based on extra data we retrieve from GraphQL.
	// We simply can't determine whether the general Connect app is a part of the "Apps" section or not without that extra data.
	// And we would like to avoid situations where the "Apps" section is expanded when it shouldn't be.
	// Keep in mind that global Connect app != general Connect app. Global app is a subset of the general app.
	// General app can be rendered in various contexts, while the global one only under the "Apps" section.
	const isMaybeAppsSectionRoute = isInitialSelectedPath(MENU_ID_APPS);
	const isGeneralConnectRoute = useIsGeneralConnectRoute();
	const isGlobalConnectAppRoute = useIsGlobalConnectAppRoute({ data: globalAppNavigationItems });
	const isAppsSectionExpanded =
		isMaybeAppsSectionRoute && (isGeneralConnectRoute ? isGlobalConnectAppRoute : true);
	const isExpandedValue = fg('navx-global-side-nav')
		? !!dndProps.isExpandOrFlyoutOpen && isExpanded
		: isExpanded;

	const sectionActions = <SectionActions />;

	return isAppsSectionExpanded ? (
		<Nav4AppsForLanding
			actionsOnHover={sectionActions}
			isExpanded={isExpandedValue}
			queryRef={data.jira}
			navigationCustomisationFragment={data.appsOrder}
			{...dndProps}
		/>
	) : (
		<Nav4AppsDefault actionsOnHover={sectionActions} isExpanded={isExpanded} {...dndProps} />
	);
}
